import { Flex, Box, Text, Skeleton, Show } from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import { ReactNode } from "react";

type VaultHeaderProps = {
  title: string;
  isLoadingAPYData: boolean;
  totalStakedEth: string;
  totalStaked: string;
  isLoadingTotalStaked: boolean;
  totalStakedLabel: string;
  apyLabel: string;
  apyValue: string;
  logo: {
    src: string;
    label: string;
  };
  tooltipComponent?: ReactNode;
  auditedUrl?: string;
};

const VaultHeader = ({
  title,
  isLoadingAPYData,
  totalStakedEth,
  totalStaked,
  isLoadingTotalStaked,
  totalStakedLabel,
  apyLabel,
  apyValue,
  logo,
  tooltipComponent,
  auditedUrl,
}: VaultHeaderProps) => {
  return (
    <Flex
      gap={{ base: "10px", md: "50px" }}
      w={"100%"}
      maxW={"736px"}
      justifyContent={"space-between"}
    >
      <Box w={"100%"}>
        <Flex
          alignItems={{ base: "start", md: "center" }}
          gap="12px"
          direction="row"
          justifyContent={{ base: "space-between", md: "flex-start" }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            gap="0.5rem"
            alignItems={{ base: "flex-start", md: "center" }}
          >
            <Text
              fontSize={{ base: "32px", md: "48px" }}
              fontWeight="500"
              lineHeight={{ base: "38.4px", md: "57.4px" }}
              letterSpacing={{ base: "-1%", md: "-3%" }}
            >
              {title}
            </Text>
            <UTMLink isExternal={true} href={auditedUrl}>
              <Flex
                p={{ base: "2px 4px", md: "4px 6px" }}
                gap="4px"
                alignItems="center"
                borderRadius="8px"
                border="1px solid"
                borderColor="#259F76"
                bgColor="#259F76"
                height="32px"
                w="fit-content"
              >
                <Text
                  color="white"
                  fontWeight="600"
                  fontSize="12px"
                  lineHeight="14.4px"
                >
                  Audited
                </Text>

                <BaseImage
                  src="/assets/shield.svg"
                  height={16}
                  width={16}
                  alt="verified"
                />
              </Flex>
            </UTMLink>
          </Flex>
          <Show below="md">
            <BaseImage height={80} width={80} src={logo.src} alt={logo.label} />
          </Show>
        </Flex>
        <Flex gap={10} mt="24px" mb={{ base: 0, md: 4 }}>
          {/* TODO: Fix values - calculate USD Value */}
          <YieldStats
            label={totalStakedLabel}
            value={totalStaked}
            subvalue={totalStakedEth}
            isLoading={isLoadingTotalStaked}
          />
          <YieldStats
            label={apyLabel}
            value={apyValue}
            isLoading={isLoadingAPYData}
            tooltipComponent={tooltipComponent}
          />
        </Flex>
      </Box>
      <Show above="md">
        <Box height={108} width={108}>
          <BaseImage height={108} width={108} src={logo.src} alt={logo.label} />
        </Box>
      </Show>
    </Flex>
  );
};

const YieldStats = ({
  label,
  value,
  subvalue,
  isLoading,
  tooltipComponent,
}: {
  label: string;
  value: string;
  subvalue?: string;
  isLoading: boolean;
  tooltipComponent?: ReactNode;
}) => {
  return isLoading ? (
    <Skeleton height="20px" w="120px" />
  ) : (
    <Flex direction={"column"} gap={"8px"}>
      <Text fontSize="12px" fontWeight="400" lineHeight={"14.4px"}>
        {label}
      </Text>
      <Flex gap="4px" alignItems="center">
        <Text
          fontSize={{ base: "22px", md: "32px" }}
          fontWeight="500"
          lineHeight={{ base: "26.4px", md: "38.4px" }}
          letterSpacing={"-1%"}
          display="inline"
        >
          {value}
        </Text>
        {tooltipComponent && tooltipComponent}
      </Flex>
      {subvalue && (
        <Text fontSize="12px" fontWeight="600" lineHeight={"14.4px"}>
          {subvalue}
        </Text>
      )}
    </Flex>
  );
};

export default VaultHeader;
